import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useLocalVersions, type LocalVersionTokens } from '../../hooks/useLocalVersions';
import { useStore } from '../../store';
import { CreateLocalVersionDialog } from './CreateLocalVersionDialog';
import { LocalVersionTokensModal } from './LocalVersionTokensModal';
import { ShareVersionDialog } from '../ShareVersionDialog';
import { ConfirmDialog } from '../ConfirmDialog';
import { createPreviewShare } from '../../utils/createPreviewShare';
import { storePreviewContext } from '../../routes/preview.$';
import { waitForReady } from '../../ws';
import type { AppVersion } from '@interlucent/organization-protocol';
import { logger } from '../../log';
import styles from './LocalVersionsSection.module.css';

const log = logger('local-versions');

interface LocalVersionsSectionProps {
  appId: string;
  projectId: string;
  quota: number;
}

export const LocalVersionsSection: React.FC<LocalVersionsSectionProps> = ({
  appId,
  projectId,
  quota,
}) => {
  const navigate = useNavigate();
  const {
    localVersions,
    createLocalVersion,
    updateLocalVersion,
    deleteLocalVersion,
    getTokens,
    cycleTokens,
  } = useLocalVersions(appId);

  const [isExpanded, setIsExpanded] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Hover-prefetch state: create preview share on hover so it's ready by click time
  const [prefetchState, setPrefetchState] = useState<Record<string, {
    status: 'idle' | 'prefetching' | 'ready' | 'failed';
    shareId?: string;
  }>>({});
  // Ref-based deduplication: tracks in-flight + completed requests per version (immune to React batching)
  const inflightShares = useRef<Map<string, Promise<string>>>(new Map());
  const abortControllerRef = useRef<AbortController>(new AbortController());

  // Create fresh AbortController on each mount (handles strict mode double-mount),
  // abort in-flight prefetch requests on unmount
  useEffect(() => {
    abortControllerRef.current = new AbortController();
    return () => {
      abortControllerRef.current.abort();
    };
  }, []);
  const [tokensModal, setTokensModal] = useState<{
    isOpen: boolean;
    versionId: string;
    tokens: LocalVersionTokens | null;
    isLoading: boolean;
    error: string | null;
    isCycling: boolean;
  }>({
    isOpen: false,
    versionId: '',
    tokens: null,
    isLoading: false,
    error: null,
    isCycling: false,
  });
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    versionId: string;
  }>({
    isOpen: false,
    versionId: '',
  });
  const [shareVersion, setShareVersion] = useState<{ versionId: string; versionName: string } | null>(null);

  const handleCreate = useCallback(async (data: { description?: string }) => {
    return createLocalVersion(data);
  }, [createLocalVersion]);

  const handleViewTokens = useCallback(async (version: AppVersion) => {
    setTokensModal({
      isOpen: true,
      versionId: version.id,
      tokens: null,
      isLoading: true,
      error: null,
      isCycling: false,
    });

    const result = await getTokens(version.id);

    if (result.success && result.tokens) {
      setTokensModal(prev => ({
        ...prev,
        tokens: result.tokens!,
        isLoading: false,
      }));
    } else {
      setTokensModal(prev => ({
        ...prev,
        isLoading: false,
        error: result.error || 'Failed to get tokens',
      }));
    }
  }, [getTokens]);

  const handleCycleTokens = useCallback(async () => {
    if (!tokensModal.versionId) return;

    setTokensModal(prev => ({ ...prev, isCycling: true }));

    const result = await cycleTokens(tokensModal.versionId);

    if (result.success) {
      // Re-fetch the new tokens
      const tokensResult = await getTokens(tokensModal.versionId);
      if (tokensResult.success && tokensResult.tokens) {
        setTokensModal(prev => ({
          ...prev,
          tokens: tokensResult.tokens!,
          isCycling: false,
        }));
      } else {
        setTokensModal(prev => ({
          ...prev,
          isCycling: false,
          error: tokensResult.error || 'Failed to get new tokens',
        }));
      }
    } else {
      setTokensModal(prev => ({
        ...prev,
        isCycling: false,
        error: result.error || 'Failed to cycle tokens',
      }));
    }
  }, [tokensModal.versionId, cycleTokens, getTokens]);

  const handleDelete = useCallback(async () => {
    if (!confirmDialog.versionId) return;

    await deleteLocalVersion(confirmDialog.versionId);
    setConfirmDialog({ isOpen: false, versionId: '' });
  }, [confirmDialog.versionId, deleteLocalVersion]);

  // Start prefetching a preview share for a version (called on hover or click)
  const startPrefetch = useCallback(async (version: AppVersion): Promise<string> => {
    // Ref-based guard: synchronous check avoids React state batching issues
    const existing = inflightShares.current.get(version.id);
    if (existing) return existing;

    setPrefetchState(prev => ({
      ...prev,
      [version.id]: { status: 'prefetching' },
    }));

    await waitForReady();

    const promise = createPreviewShare({
      projectId,
      appId,
      versionId: version.id,
      expiresInMinutes: 1440, // 24 hours
      signal: abortControllerRef.current.signal,
    }).then(({ id }) => {
      setPrefetchState(prev => ({
        ...prev,
        [version.id]: { status: 'ready', shareId: id },
      }));
      return id;
    }).catch((err) => {
      // Remove from inflight on failure so retry is possible
      inflightShares.current.delete(version.id);
      // AbortErrors are expected on unmount — skip log and state update
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      log.error('failed to prefetch preview share', err);
      setPrefetchState(prev => ({
        ...prev,
        [version.id]: { status: 'failed' },
      }));
      throw err;
    });

    inflightShares.current.set(version.id, promise);
    return promise;
  }, [projectId, appId]);

  // Hover handler — kick off prefetch in background
  const handlePreviewHover = useCallback((version: AppVersion) => {
    startPrefetch(version)?.catch(() => {}); // errors already handled in startPrefetch
  }, [startPrefetch]);

  // Click handler — navigate immediately if ready, otherwise wait for prefetch
  const handlePreviewClick = useCallback(async (version: AppVersion) => {
    const current = prefetchState[version.id];
    let shareId: string | undefined;

    if (current?.status === 'ready' && current.shareId) {
      shareId = current.shareId;
    } else {
      // Touch device path or prefetch still in flight
      try {
        const existingPromise = inflightShares.current.get(version.id);
        if (existingPromise) {
          shareId = await existingPromise;
        } else {
          // No prefetch started yet (touch device) — start and wait
          shareId = await startPrefetch(version);
        }
      } catch (err) {
        log.error('failed to create preview share', err);
        return;
      }
    }

    if (!shareId) return;

    storePreviewContext({
      title: `${version.id} (Local)`,
      projectId,
      appId,
      versionId: version.id,
    });
    navigate({
      to: '/preview/$',
      params: { _splat: shareId },
    });
  }, [navigate, projectId, appId, prefetchState, startPrefetch]);

  const isAtQuota = quota !== -1 && localVersions.length >= quota;
  const wsConnected = useStore(s => s.connectionState === 'connected');

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className={styles.section}>
      <div
        className={styles.sectionHeader}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
        aria-expanded={isExpanded}
      >
        <div className={styles.sectionHeaderLeft}>
          <h3 className={styles.sectionTitle}>Local Versions</h3>
          <span className={styles.sectionCount}>
            ({localVersions.length}{quota !== -1 ? ` / ${quota}` : ''})
          </span>
        </div>
        <svg
          className={`${styles.toggleIcon} ${isExpanded ? styles.expanded : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {isExpanded && (
        <div className={styles.sectionContent}>
          <p className={styles.description}>
            Local versions are for development and testing. They generate browser admission and worker access tokens without requiring file uploads.
          </p>

          <div className={styles.actionBar}>
            <button
              onClick={() => setShowCreateDialog(true)}
              disabled={!wsConnected || isAtQuota}
              className={styles.button}
            >
              Add Local Version
            </button>
          </div>

          {isAtQuota && (
            <div className={styles.quotaInfo}>
              Local version quota reached. Delete an existing version to create a new one.
            </div>
          )}

          {localVersions.length === 0 ? (
            <div className={styles.emptyState}>
              No local versions yet. Create one to start testing locally.
            </div>
          ) : (
            <div className={styles.cardsContainer}>
              {localVersions.map((version) => (
                <div key={version.id} className={styles.localVersionCard}>
                  {/* Preview thumbnail - prefetches share on hover, navigates on click */}
                  <button
                    type="button"
                    className={[
                      styles.thumbnailButton,
                      prefetchState[version.id]?.status === 'prefetching' ? styles.prefetching : '',
                      prefetchState[version.id]?.status === 'failed' ? styles.failed : '',
                    ].join(' ')}
                    title="Preview local version"
                    disabled={!wsConnected}
                    onMouseEnter={() => handlePreviewHover(version)}
                    onClick={() => handlePreviewClick(version)}
                  >
                    <svg className={styles.thumbnailIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                  <div className={styles.versionInfo}>
                    <div className={styles.versionNameRow}>
                      <span className={styles.versionName}>{version.id}</span>
                      <span className={styles.localBadge}>Local</span>
                    </div>
                    {version.description && (
                      <div className={styles.versionDescription}>{version.description}</div>
                    )}
                    <div className={styles.versionMeta}>
                      Created {formatDate(version.createdAt)}
                      {version.localTokensGeneratedAt && (
                        <> &middot; Tokens generated {formatDate(version.localTokensGeneratedAt)}</>
                      )}
                    </div>
                  </div>
                  <div className={styles.cardActions}>
                    <button
                      onClick={() => setShareVersion({ versionId: version.id, versionName: `${version.id} (Local)` })}
                      disabled={!wsConnected}
                      className={styles.buttonSmall}
                      title="Create share link"
                    >
                      Share
                    </button>
                    <button
                      onClick={() => handleViewTokens(version)}
                      disabled={!wsConnected}
                      className={styles.buttonSmall}
                    >
                      View Tokens
                    </button>
                    <button
                      onClick={() => setConfirmDialog({
                        isOpen: true,
                        versionId: version.id,
                      })}
                      className={`${styles.buttonSmall} ${styles.buttonDelete}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <CreateLocalVersionDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreate={handleCreate}
      />

      <LocalVersionTokensModal
        isOpen={tokensModal.isOpen}
        onClose={() => setTokensModal(prev => ({ ...prev, isOpen: false }))}
        versionId={tokensModal.versionId}
        tokens={tokensModal.tokens}
        isLoading={tokensModal.isLoading}
        error={tokensModal.error}
        onCycleTokens={handleCycleTokens}
        isCycling={tokensModal.isCycling}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Local Version"
        message={`Are you sure you want to delete "${confirmDialog.versionId}"? This will invalidate any tokens generated for this version.`}
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDialog({ isOpen: false, versionId: '' })}
      />

      {shareVersion && (
        <ShareVersionDialog
          isOpen={true}
          versionId={shareVersion.versionId}
          versionName={shareVersion.versionName}
          appId={appId}
          projectId={projectId}
          onClose={() => setShareVersion(null)}
        />
      )}
    </div>
  );
};
